// Used for only global custom JS

(() => {
  const PDF_CONTEXT_CLASS = 'pdf-link-context';
  const DECORATIVE_SELECTOR = '.sr-only, svg, img, picture, [aria-hidden="true"]';
  const PDF_SUFFIX = ' (PDF)';
  const NEW_TAB_TEXT = ', opens in new tab';
  const LANTERN_QUIZ_HASH = '#open-quiz-tools/perfect-product-finder/shelf-awareness-quiz';
  const LANTERN_QUIZ_TRIGGER_SELECTOR = `[data-lantern-quiz-trigger], a[href="${LANTERN_QUIZ_HASH}"]`;
  const LANTERN_QUIZ_IFRAME_SELECTOR = 'iframe[src*="/tools/perfect-product-finder/shelf-awareness-quiz"]';
  const DISMISS_CONTROL_SELECTOR = 'button, [role="button"], a[href], [tabindex], [data-close]';
  const CLOSE_TEXT_MATCHER = /\b(close|dismiss)\b|^[×x]$/i;

  let lastLanternQuizTrigger = null;

  function isPdfHref(href) {
    return /\.pdf(?:$|[?#])/i.test(href || '');
  }

  function getVisibleLinkText(anchor) {
    const clone = anchor.cloneNode(true);
    clone.querySelectorAll(DECORATIVE_SELECTOR).forEach((element) => element.remove());
    return clone.textContent.replace(/\s+/g, ' ').trim();
  }

  function insertBeforeDecorative(anchor, node) {
    anchor.insertBefore(node, anchor.querySelector(DECORATIVE_SELECTOR));
  }

  function ensureVisiblePdfText(anchor, visibleText) {
    if (!visibleText || /\bpdf\b/i.test(visibleText)) return;
    insertBeforeDecorative(anchor, document.createTextNode(PDF_SUFFIX));
  }

  function ensureNewTabContext(anchor) {
    if (anchor.target !== '_blank') return;
    if (anchor.querySelector(`.${PDF_CONTEXT_CLASS}`)) return;

    const context = document.createElement('span');
    context.className = `sr-only ${PDF_CONTEXT_CLASS}`;
    context.textContent = NEW_TAB_TEXT;
    insertBeforeDecorative(anchor, context);
  }

  function ensureAriaLabel(anchor, href, visibleText) {
    const originalLabel = anchor.getAttribute('aria-label');
    if (!originalLabel && visibleText) return;

    let label = originalLabel;

    if (!label) {
      label = decodeURIComponent((href || '').split('/').pop()?.split(/[?#]/)[0] || 'PDF');
      label = label.replace(/\.[^.]+$/, '').replace(/[-_]+/g, ' ').trim() || 'PDF';
    }

    if (!/\bpdf\b/i.test(label)) {
      label += ', PDF';
    }

    if (anchor.target === '_blank' && !/opens in new tab/i.test(label)) {
      label += NEW_TAB_TEXT;
    }

    if (label !== originalLabel) {
      anchor.setAttribute('aria-label', label);
    }
  }

  function enhancePdfLink(anchor) {
    if (anchor.hasAttribute('data-a11y-download')) return;

    const href = anchor.getAttribute('href');
    if (!isPdfHref(href)) return;

    const visibleText = getVisibleLinkText(anchor);
    ensureVisiblePdfText(anchor, visibleText);
    ensureAriaLabel(anchor, href, visibleText);

    if (!anchor.hasAttribute('aria-label')) {
      ensureNewTabContext(anchor);
    }
  }

  function enhancePdfLinks(root = document) {
    if (root instanceof HTMLAnchorElement) {
      enhancePdfLink(root);
      return;
    }

    root.querySelectorAll('a[href]').forEach(enhancePdfLink);
  }

  function initPdfLinkAccessibility() {
    enhancePdfLinks(document);

    new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList') {
          mutation.addedNodes.forEach((node) => {
            if (node instanceof Element) enhancePdfLinks(node);
          });
        }

        if (mutation.type === 'attributes' && mutation.target instanceof HTMLAnchorElement) {
          enhancePdfLink(mutation.target);
        }
      });
    }).observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['href', 'target', 'aria-label']
    });
  }

  function getElementFingerprint(element) {
    return [
      element.id,
      typeof element.className === 'string' ? element.className : '',
      element.getAttribute('name'),
      element.getAttribute('title'),
      element.getAttribute('aria-label'),
      element.textContent?.trim()
    ].join(' ');
  }

  function isVisibleElement(element) {
    if (!element?.isConnected || element.hidden || element.getAttribute('aria-hidden') === 'true') {
      return false;
    }

    const rect = element.getBoundingClientRect();
    if (rect.width < 1 || rect.height < 1) return false;

    const style = window.getComputedStyle(element);
    return style.display !== 'none' && style.visibility !== 'hidden' && style.opacity !== '0';
  }

  function findLanternQuizIframe() {
    return Array.from(document.querySelectorAll(LANTERN_QUIZ_IFRAME_SELECTOR))
      .find((iframe) => iframe instanceof HTMLIFrameElement && isVisibleElement(iframe));
  }

  function getLanternQuizContent(iframe) {
    const viewportArea = window.innerWidth * window.innerHeight;
    const iframeRect = iframe.getBoundingClientRect();
    let content = iframe;
    let candidate = iframe.parentElement;

    while (candidate && candidate !== document.body) {
      if (!isVisibleElement(candidate)) {
        candidate = candidate.parentElement;
        continue;
      }

      const rect = candidate.getBoundingClientRect();
      const area = rect.width * rect.height;
      const isViewportOverlay = area >= viewportArea * 0.85 && rect.width >= window.innerWidth * 0.9;
      const containsIframe = (
        rect.left <= iframeRect.left &&
        rect.right >= iframeRect.right &&
        rect.top <= iframeRect.top &&
        rect.bottom >= iframeRect.bottom
      );

      if (!isViewportOverlay && containsIframe) {
        content = candidate;
      }

      candidate = candidate.parentElement;
    }

    return content;
  }

  function getRectDistance(firstRect, secondRect) {
    const firstX = firstRect.left + firstRect.width / 2;
    const firstY = firstRect.top + firstRect.height / 2;
    const secondX = secondRect.left + secondRect.width / 2;
    const secondY = secondRect.top + secondRect.height / 2;

    return Math.hypot(firstX - secondX, firstY - secondY);
  }

  function findLanternQuizDismissButton(iframe, content) {
    const contentRect = content.getBoundingClientRect();
    const candidates = Array.from(document.querySelectorAll(DISMISS_CONTROL_SELECTOR))
      .filter((element) => isVisibleElement(element) && CLOSE_TEXT_MATCHER.test(getElementFingerprint(element)))
      .map((element) => ({
        element,
        distance: getRectDistance(element.getBoundingClientRect(), contentRect)
      }))
      .filter(({ element, distance }) => {
        if (element.closest('iframe')) return false;
        if (content.contains(element)) return true;
        return distance <= Math.max(contentRect.width, contentRect.height, 300);
      })
      .sort((first, second) => first.distance - second.distance);

    return candidates[0]?.element || null;
  }

  function setLanternQuizTriggerExpanded(expanded) {
    document.querySelectorAll(LANTERN_QUIZ_TRIGGER_SELECTOR).forEach((trigger) => {
      trigger.setAttribute('aria-expanded', String(expanded));
    });
  }

  function closeLanternQuizPopup() {
    const iframe = findLanternQuizIframe();
    if (!iframe) return false;

    const content = getLanternQuizContent(iframe);
    const dismissButton = findLanternQuizDismissButton(iframe, content);
    if (!dismissButton) return false;

    dismissButton.click();
    setLanternQuizTriggerExpanded(false);

    if (lastLanternQuizTrigger?.isConnected) {
      lastLanternQuizTrigger.focus({ preventScroll: true });
    }

    return true;
  }

  function initLanternQuizCloseBehavior() {
    if (window.location.hash === LANTERN_QUIZ_HASH) {
      setLanternQuizTriggerExpanded(true);
    }

    window.addEventListener('hashchange', () => {
      if (window.location.hash !== LANTERN_QUIZ_HASH) return;

      setLanternQuizTriggerExpanded(true);
    });

    document.addEventListener('click', (event) => {
      const trigger = event.target.closest?.(LANTERN_QUIZ_TRIGGER_SELECTOR);
      if (!trigger) return;

      lastLanternQuizTrigger = trigger;
      setLanternQuizTriggerExpanded(true);
    }, true);

    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      if (!closeLanternQuizPopup()) return;

      event.preventDefault();
      event.stopPropagation();
    }, true);
  }

  function fixVideoPosters() {
    document.querySelectorAll('video > img:not([alt])').forEach(function(img) {
      img.setAttribute('alt', '');
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() {
      initPdfLinkAccessibility();
      initLanternQuizCloseBehavior();
      fixVideoPosters();
    }, { once: true });
  } else {
    initPdfLinkAccessibility();
    initLanternQuizCloseBehavior();
    fixVideoPosters();
  }

  document.addEventListener('page:loaded', () => {
    enhancePdfLinks(document);
    fixVideoPosters();
  });
})();

// Product-card hover carousels: Flickity bullets are native buttons, but Concept
// nests them in the product <a> and hides them until hover. Keep mouse preview,
// and make the visible bullets a single keyboard tab stop.
(() => {
  const SecondaryMedia = customElements.get('secondary-media');
  if (!SecondaryMedia?.prototype?.init) return;

  const originalInit = SecondaryMedia.prototype.init;
  const originalDisconnected = SecondaryMedia.prototype.disconnectedCallback;

  function visibleDots(dotsNav) {
    return Array.from(dotsNav.querySelectorAll('.flickity-page-dot')).filter((dot) => {
      return window.getComputedStyle(dot).display !== 'none';
    });
  }

  function syncDotRoving(dotsNav) {
    const allDots = Array.from(dotsNav.querySelectorAll('.flickity-page-dot'));
    const dots = visibleDots(dotsNav);
    if (dots.length === 0) return;

    const current = dots.find((dot) => dot.classList.contains('is-selected')) || dots[0];
    allDots.forEach((dot) => {
      dot.tabIndex = dot === current ? 0 : -1;
    });
  }

  function labelForDot(host, index) {
    const template = host.getAttribute('data-dot-label');
    if (template) return template.replaceAll('__n__', String(index + 1));
    return `View slide ${index + 1}`;
  }

  function enhanceDots(host) {
    if (!host.carousel || host.classList.contains('without-dots')) return;

    const dotsNav = host.querySelector('.flickity-page-dots');
    if (!dotsNav || host._a11yDotsNav === dotsNav) return;

    host._a11yDotsNav = dotsNav;
    const allDots = Array.from(dotsNav.querySelectorAll('.flickity-page-dot'));
    allDots.forEach((dot, index) => {
      dot.setAttribute('aria-label', labelForDot(host, index));
    });

    dotsNav.addEventListener('keydown', (event) => {
      if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;

      const dots = visibleDots(dotsNav);
      const currentIndex = dots.indexOf(event.target.closest('.flickity-page-dot'));
      if (currentIndex === -1 || dots.length < 2) return;

      const rtl = document.documentElement.getAttribute('dir') === 'rtl';
      const prevKey = rtl ? 'ArrowRight' : 'ArrowLeft';
      const nextKey = rtl ? 'ArrowLeft' : 'ArrowRight';

      let nextIndex = currentIndex;
      if (event.key === prevKey) nextIndex = (currentIndex - 1 + dots.length) % dots.length;
      else if (event.key === nextKey) nextIndex = (currentIndex + 1) % dots.length;
      else if (event.key === 'Home') nextIndex = 0;
      else if (event.key === 'End') nextIndex = dots.length - 1;

      event.preventDefault();
      const nextDot = dots[nextIndex];
      const slideIndex = allDots.indexOf(nextDot);
      if (slideIndex === -1) return;

      host.carousel.select(slideIndex);
      nextDot.focus();
    });

    host.carousel.on('select', () => syncDotRoving(dotsNav));
    syncDotRoving(dotsNav);
  }

  function bindMediaHover(host) {
    host.removeEventListener('mousemove', host.onMoveHandler);
    host.removeEventListener('mouseleave', host.onLeaveHandler);

    const media = host.closest('.product-card__media');
    if (!media || !host.carousel) return;

    host._cardMedia = media;
    host._boundMediaMove = (event) => {
      if (host.matches(':focus-within')) return;
      if (event.target.closest('.flickity-page-dot')) return;
      host.onMoveHandler(event);
    };
    host._boundMediaLeave = host.onLeaveHandler.bind(host);
    media.addEventListener('mousemove', host._boundMediaMove);
    media.addEventListener('mouseleave', host._boundMediaLeave);
  }

  function unbindMediaHover(host) {
    if (!host._cardMedia) return;

    host._cardMedia.removeEventListener('mousemove', host._boundMediaMove);
    host._cardMedia.removeEventListener('mouseleave', host._boundMediaLeave);
    host._cardMedia = null;
    host._boundMediaMove = null;
    host._boundMediaLeave = null;
  }

  SecondaryMedia.prototype.init = function patchedSecondaryMediaInit() {
    originalInit.call(this);
    if (!this.carousel) return;

    bindMediaHover(this);
    enhanceDots(this);
  };

  SecondaryMedia.prototype.disconnectedCallback = function patchedSecondaryMediaDisconnected() {
    unbindMediaHover(this);
    if (typeof originalDisconnected === 'function') originalDisconnected.call(this);
  };
})();
